import { useEffect, useState } from "react";

import styles from "../../styles/components/AdminDetailModals.module.css";
import ModalOverlay from "../../ui/Modals/ModalOverlay";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { accountsService } from "../../services/accounts.service";
import { usersService } from "../../services/users.service";
import Avatar from "../../ui/avatar/Avatar";
import BaseInput from "../../ui/inputs/BaseInput/BaseInput";
import DropdownSelect from "../../ui/inputs/DropdownSelect";
import HoverIconButton from "../../ui/buttons/HoverIconButton";
import TimezoneSelect from "../../ui/inputs/TimezoneSelect";
import BusinessHoursEditor, { normalizeBusinessHours, validateBusinessHours } from "../../ui/businessHours/BusinessHoursEditor";
import { extractApiErrorMessage } from "../../utils/apiError";
import { ConfirmDeleteModal } from "./AdminDetailModals";
import MailAccountAssignmentFlags, { EMPTY_ASSIGNMENT_DRAFT } from "./MailAccountAssignmentFlags";
import { assignmentDisplayName, describeMailAccountConnection, mailAccountTitle, userDisplayName } from "./mailAccountAdminHelpers";

const CONFIG_FIELDS = ["name", "smtp", "smtp_port", "imap", "imap_port", "timezone"];

function buildConfigDraft(mailAccount) {
  return {
    name: mailAccount.name ?? "",
    password: "",
    smtp: mailAccount.smtp ?? "",
    smtp_port: mailAccount.smtp_port ?? "",
    imap: mailAccount.imap ?? "",
    imap_port: mailAccount.imap_port ?? "",
    timezone: mailAccount.timezone ?? "",
    business_hours: normalizeBusinessHours(mailAccount.business_hours),
  };
}

export default function MailAccountDetailModal({ mailAccount, onClose, onUpdateMailAccount, onDeleted }) {
  const [actionError, setActionError] = useState(null);
  const [addingUser, setAddingUser] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);
  const [assignmentDraft, setAssignmentDraft] = useState(EMPTY_ASSIGNMENT_DRAFT);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState(null);
  const [showConfigErrors, setShowConfigErrors] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const mailAccountId = mailAccount?.id;

  useEffect(() => {
    setActionError(null);
    setAddingUser(false);
    setAvailableUsers([]);
    setAssignmentDraft(EMPTY_ASSIGNMENT_DRAFT);
    setShowDeleteConfirm(false);
    setEditingConfig(false);
    setConfigDraft(null);
    setShowConfigErrors(false);
  }, [mailAccountId]);

  if (!mailAccount) return null;

  const assignments = mailAccount.users ?? [];
  const title = mailAccountTitle(mailAccount);
  const connection = describeMailAccountConnection(mailAccount);
  const badgeClassName = `${styles.mailAccountBadge} ${connection.warning ? styles.mailAccountBadgeWarning : ""}`;

  const updateUsers = (updater) => {
    onUpdateMailAccount?.((current) => ({ ...current, users: updater(current.users ?? []) }));
  };

  const setAssignmentFlag = (assignmentId, field, value) => {
    updateUsers((users) =>
      users.map((assignment) => (assignment.id === assignmentId ? { ...assignment, [field]: value } : assignment)),
    );
  };

  const handleToggleFlag = async (assignment, field) => {
    const nextValue = !assignment[field];
    setAssignmentFlag(assignment.id, field, nextValue);
    setActionError(null);
    try {
      await usersService.updateMailCorpUserAssignment(mailAccount.id, assignment.id, { [field]: nextValue });
    } catch (error) {
      setAssignmentFlag(assignment.id, field, !nextValue);
      setActionError(extractApiErrorMessage(error) || "No se pudo actualizar el permiso.");
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    setActionError(null);
    try {
      await usersService.removeMailCorpUser(mailAccount.id, assignment.id);
      updateUsers((users) => users.filter((existing) => existing.id !== assignment.id));
    } catch (error) {
      setActionError(extractApiErrorMessage(error) || "No se pudo quitar el usuario de la casilla.");
    }
  };

  const handleOpenAddUser = async () => {
    setAddingUser(true);
    setEditingConfig(false);
    setAssignmentDraft(EMPTY_ASSIGNMENT_DRAFT);
    setActionError(null);
    setLoadingAvailableUsers(true);
    try {
      const accountUsers = await accountsService.listUsers(mailAccount.account, { includeInherited: true });
      const assignedUserIds = new Set(assignments.map((assignment) => assignment.user_id));
      setAvailableUsers(accountUsers.filter((user) => !assignedUserIds.has(user.id)));
    } catch (error) {
      setAvailableUsers([]);
      setActionError(extractApiErrorMessage(error) || "No se pudieron cargar los usuarios de la cuenta.");
    } finally {
      setLoadingAvailableUsers(false);
    }
  };

  const handleAddUser = async () => {
    if (!assignmentDraft.userId) return;
    setSavingAssignment(true);
    setActionError(null);
    try {
      const assignment = await usersService.addMailCorpUser(mailAccount.id, {
        user_id: Number(assignmentDraft.userId),
        is_mail_account_admin: assignmentDraft.is_mail_account_admin,
        can_send: assignmentDraft.can_send,
        can_receive: assignmentDraft.can_receive,
      });
      updateUsers((users) => [...users, assignment]);
      setAddingUser(false);
    } catch (error) {
      setActionError(extractApiErrorMessage(error) || "No se pudo asignar el usuario a la casilla.");
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleOpenConfig = () => {
    setConfigDraft(buildConfigDraft(mailAccount));
    setShowConfigErrors(false);
    setEditingConfig(true);
    setAddingUser(false);
    setActionError(null);
  };

  const handleCloseConfig = () => {
    setEditingConfig(false);
    setConfigDraft(null);
    setShowConfigErrors(false);
  };

  const setConfigField = (field) => (value) => setConfigDraft((current) => ({ ...current, [field]: value }));

  const handleSaveConfig = async () => {
    const hoursValidation = validateBusinessHours(configDraft.business_hours);
    if (!configDraft.timezone || !hoursValidation.valid) {
      setShowConfigErrors(true);
      setActionError(!configDraft.timezone ? "La zona horaria es obligatoria." : hoursValidation.message);
      return;
    }
    setSavingConfig(true);
    setActionError(null);
    const payload = Object.fromEntries(CONFIG_FIELDS.map((field) => [field, configDraft[field]]));
    payload.business_hours = configDraft.business_hours;
    if (configDraft.password) payload.password = configDraft.password;
    try {
      const saved = await usersService.updateMailCorp(mailAccount.id, payload);
      onUpdateMailAccount?.((current) => ({
        ...current,
        ...Object.fromEntries(CONFIG_FIELDS.map((field) => [field, saved?.[field] ?? payload[field]])),
        business_hours: saved?.business_hours ?? payload.business_hours,
      }));
      handleCloseConfig();
    } catch (error) {
      setActionError(extractApiErrorMessage(error) || "No se pudo guardar la configuración.");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDelete = async () => {
    setActionError(null);
    try {
      await usersService.removeMailCorp(mailAccount.id);
      onDeleted?.(mailAccount.id);
      onClose?.();
    } catch (error) {
      setShowDeleteConfirm(false);
      setActionError(extractApiErrorMessage(error) || "No se pudo eliminar la casilla.");
    }
  };

  const availableUserOptions = availableUsers.map((user) => ({
    value: String(user.id),
    label: userDisplayName(user),
  }));

  const renderAssignment = (assignment) => {
    const displayName = assignmentDisplayName(assignment);
    return (
      <div key={assignment.id} className={`${styles.mailUserItem} ${HoverIconButton.rowClassName}`}>
        <Avatar name={displayName} size={36} />
        <div className={styles.mailUserInfo}>
          <span className={styles.accountName}>{displayName}</span>
          <span className={styles.accountEmail}>{assignment.email}</span>
        </div>
        <MailAccountAssignmentFlags
          values={assignment}
          onToggle={(field) => handleToggleFlag(assignment, field)}
          labelSuffix={`de ${displayName} en ${mailAccount.email}`}
        />
        <HoverIconButton
          icon={<TrashIcon />}
          onClick={() => handleRemoveAssignment(assignment)}
          aria-label={`Quitar a ${displayName} de ${mailAccount.email}`}
        />
      </div>
    );
  };

  const renderAddUserForm = () => (
    <div className={styles.mailInlineForm}>
      {loadingAvailableUsers ? (
        <div className={styles.noAccounts}>Cargando usuarios...</div>
      ) : (
        <DropdownSelect
          placeholder="Usuario..."
          options={availableUserOptions}
          value={assignmentDraft.userId}
          onChange={(value) => setAssignmentDraft((draft) => ({ ...draft, userId: value }))}
        />
      )}
      {!loadingAvailableUsers && availableUserOptions.length === 0 && (
        <div className={styles.noAccounts}>Todos los usuarios de la cuenta ya tienen acceso.</div>
      )}
      <MailAccountAssignmentFlags
        values={assignmentDraft}
        onToggle={(field) => setAssignmentDraft((draft) => ({ ...draft, [field]: !draft[field] }))}
        labelSuffix="para el nuevo usuario"
      />
      <div className={styles.mailInlineActions}>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={handleAddUser}
          disabled={savingAssignment || !assignmentDraft.userId}
        >
          {savingAssignment ? "Agregando..." : "Agregar"}
        </button>
        <button type="button" className={styles.editBtn} onClick={() => setAddingUser(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );

  const renderConfigForm = () => {
    const hoursValidation = validateBusinessHours(configDraft.business_hours);
    return (
      <div className={styles.mailConfigForm}>
        <div className={styles.mailConfigSectionTitle}>Configuración de la casilla</div>
        <div className={styles.mailConfigGrid}>
          <BaseInput label="Nombre" value={configDraft.name} onChange={setConfigField("name")} />
          <BaseInput
            label="Contraseña"
            type="password"
            value={configDraft.password}
            onChange={setConfigField("password")}
            validateOnChange={false}
            placeholder="Sin cambios"
          />
          <BaseInput label="SMTP" value={configDraft.smtp} onChange={setConfigField("smtp")} />
          <BaseInput label="Puerto SMTP" type="number" value={configDraft.smtp_port} onChange={setConfigField("smtp_port")} />
          <BaseInput label="IMAP" value={configDraft.imap} onChange={setConfigField("imap")} />
          <BaseInput label="Puerto IMAP" type="number" value={configDraft.imap_port} onChange={setConfigField("imap_port")} />
        </div>
        <TimezoneSelect
          value={configDraft.timezone}
          onChange={setConfigField("timezone")}
          required
          error={showConfigErrors && !configDraft.timezone ? "La zona horaria es obligatoria" : undefined}
        />
        <div className={styles.mailConfigSectionTitle}>Horarios laborales</div>
        <BusinessHoursEditor
          value={configDraft.business_hours}
          onChange={setConfigField("business_hours")}
          errors={showConfigErrors ? hoursValidation.rowErrors : undefined}
        />
        <div className={styles.mailInlineActions}>
          <button type="button" className={styles.saveBtn} onClick={handleSaveConfig} disabled={savingConfig}>
            {savingConfig ? "Guardando..." : "Guardar configuración"}
          </button>
          <button type="button" className={styles.editBtn} onClick={handleCloseConfig} disabled={savingConfig}>
            Cancelar
          </button>
        </div>
      </div>
    );
  };

  return (
    <ModalOverlay className={styles.overlay} onClose={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerInfo}>
            <div className={styles.modalName}>{title}</div>
            <div className={styles.modalUsername}>
              <span>{mailAccount.email}</span>
            </div>
            <div className={styles.mailAccountHeaderMeta}>
              <span className={styles.pill} title={mailAccount.account_name ?? "-"}>
                {mailAccount.account_name ?? "-"}
              </span>
              <span className={badgeClassName}>{connection.label}</span>
              {mailAccount.smtp && (
                <span className={styles.mailAccountBadge}>
                  {mailAccount.smtp}
                  {mailAccount.smtp_port ? `:${mailAccount.smtp_port}` : ""}
                </span>
              )}
              <span className={styles.mailAccountBadge}>
                {assignments.length === 1 ? "1 usuario" : `${assignments.length} usuarios`}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles.trashBtn}
            aria-label="Eliminar casilla"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <TrashIcon className={styles.trashIcon} />
          </button>
        </div>

        <div className={styles.mailAccountsSection}>
          {actionError && (
            <div className={styles.formError} role="alert">
              {actionError}
            </div>
          )}

          {editingConfig && configDraft ? (
            renderConfigForm()
          ) : (
            <>
              <div className={styles.accountsSectionTitle}>Usuarios con acceso</div>

              {assignments.length === 0 && (
                <div className={styles.noAccounts}>Sin usuarios asignados: esta casilla no tiene responsable.</div>
              )}
              {assignments.map(renderAssignment)}

              {addingUser ? (
                renderAddUserForm()
              ) : (
                <div className={styles.mailActionsRow}>
                  <button type="button" className={styles.addAccountBtn} onClick={handleOpenAddUser}>
                    <AddIcon className={styles.addIcon} />
                    Agregar usuario
                  </button>
                  <button type="button" className={styles.addAccountBtn} onClick={handleOpenConfig}>
                    <EditIcon className={styles.addIcon} />
                    Editar configuración
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <ConfirmDeleteModal
          title="Eliminar casilla"
          description="Se eliminan la casilla, sus asignaciones de usuarios y todos los correos asociados. Esta acción no se puede deshacer."
          name={mailAccount.email}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </ModalOverlay>
  );
}
